import { Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { useSelector } from "react-redux";
import { Box, CircularProgress } from "@mui/material";
import { getRouterPaths } from "./routerPaths";
import MainLayout from "../layout/main.layout";
import DigitalTwinLayout from "../layout/digitalTwin.layout";
import HomePage from "../pages/home.page";
import UsersPage from "../pages/users.page";
import LandsPage from "../pages/lands.page";
import FieldsTypePage from "../pages/fieldsType.page";
import SettingsPage from "../pages/settings.page";
import LoginPage from "../pages/login.page";
import AddUserPage from "../pages/addUser.page";
import { createSelector } from "@reduxjs/toolkit";
import ProfilePage from "../pages/profile.page";
import UserLandsPage from "../pages/customer/userLands.page";
import NotFoundPage from "../pages/notFound.page";
import InitialPasswordChangePage from "../pages/initialPasswordChange.page";
import CultureListPage from "../pages/cultureList.page";
import IrrigationTypePage from "../pages/irrigationType.page";
import AddCulturePage from "../pages/addCulture.page";
import UpdateUserPage from "../pages/updateUser.page";
import { routePermissions } from "./routeConfig";
import AddIrrigationPage from "../pages/addIrrigation.page";
import UpdateIrrigationPage from "../pages/updateIrrigation.page";
import AddFieldTypePage from "../pages/addFieldType.page";
import UpdateFieldTypePage from "../pages/updateFieldType.page";
import UpdateCulturePage from "../pages/updateCulture.page";
import AddFieldPage from "../pages/customer/addField.page";
import FieldPage from "../pages/customer/field.page";
import UpdateFieldPage from "../pages/customer/updateField.page";
import ToolsPage from "../pages/customer/tools.page";
import AddToolPage from "../pages/customer/addTool.page";
import FieldNotebookPage from "../pages/customer/fieldNotebook.page";
import UpdateToolPage from "../pages/customer/updateSensor.page";
import AddToolRequestPage from "../pages/customer/addToolRequest.page";
import DigitalTwinPage from "../pages/digitalTwin/digitalTwinHome.page";
import LivestockDetailPage from "../pages/digitalTwin/livestockDetail.page";
import MonitoringSection from "../pages/monitoringSection.page";
import ChangePasswordPage from "../pages/changePassword.page";
import ForgotPasswordPage from "../pages/forgotPassword.page";
import ResetPasswordPage from "../pages/resetPassword.page";
import AddNotePage from "../pages/customer/addNote.page";
import CropDetailPage from "../pages/digitalTwin/cropDetail.page";

const selectAuth = (state) => state.auth;
const selectUserData = (state) => state.me?.data?.data;

const selectIsAuthenticated = createSelector(
  selectAuth,
  (auth) => auth.isAuthenticated,
);
const selectIsAuthInitialized = createSelector(
  selectAuth,
  (auth) => auth.isInitialized,
);
const selectUserType = createSelector(
  selectUserData,
  (userData) => userData?.type || null,
);

const FullPageLoader = () => (
  <Box
    width="100vw"
    height="100vh"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <CircularProgress />
  </Box>
);

const ProtectedRoute = ({ children, isNotFoundPage = false }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAuthInitialized = useSelector(selectIsAuthInitialized);
  const type = useSelector(selectUserType);
  const routerPaths = getRouterPaths(type);
  const location = useLocation();

  if (!isAuthInitialized || (isAuthenticated && !type)) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate to={routerPaths.login} state={{ from: location }} replace />
    );
  }

  if (isNotFoundPage) {
    return children;
  }

  const allowedRoutes = routePermissions[type] || [];

  const isRouteAllowed = allowedRoutes.some((route) => {
    if (!route.includes(":")) {
      return route === location.pathname;
    }

    const routeSegments = route.split("/");
    const pathSegments = location.pathname.split("/");

    if (routeSegments.length !== pathSegments.length) {
      return false;
    }

    return routeSegments.every((segment, index) => {
      return segment.startsWith(":") || segment === pathSegments[index];
    });
  });
  if (!isRouteAllowed) {
    return <Navigate to={routerPaths.home} replace />;
  }
  return children;
};

const ApplicationRoutes = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAuthInitialized = useSelector(selectIsAuthInitialized);
  const type = useSelector(selectUserType);
  const routerPaths = getRouterPaths(type);

  if (!isAuthInitialized || (isAuthenticated && !type)) {
    return <FullPageLoader />;
  }

  return (
    <Routes>
      {/* Route pubbliche */}
      <Route
        path="/"
        element={
          isAuthenticated && type ? (
            <Navigate to={routerPaths.home} replace />
          ) : (
            <Navigate to={routerPaths.login} replace />
          )
        }
      />
      <Route
        path={routerPaths.login}
        element={
          isAuthenticated && type ? (
            <Navigate to={routerPaths.home} replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path={routerPaths.initialPasswordChange}
        element={<InitialPasswordChangePage />}
      />
      <Route
        path={routerPaths.forgotPassword}
        element={<ForgotPasswordPage />}
      />
      <Route
        path={routerPaths.changePassword}
        element={<ChangePasswordPage />}
      />
      <Route path={routerPaths.resetPassword} element={<ResetPasswordPage />} />
      {/* Route protette - senza prefisso */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout>
              <Outlet />
            </MainLayout>
          </ProtectedRoute>
        }
      >
        <Route path="/home" element={<HomePage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/addUser" element={<AddUserPage />} />
        <Route path="/lands" element={<LandsPage />} />
        <Route path="/fieldType" element={<FieldsTypePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/userLands" element={<UserLandsPage />} />
        <Route path="/userLands/addField" element={<AddFieldPage />} />
        <Route path="/userLands/:id" element={<FieldPage />} />
        <Route
          path="/userLands/updateField/:id"
          element={<UpdateFieldPage />}
        />
        <Route path="/tools" element={<ToolsPage />} />
        <Route path="/tools/addTool" element={<AddToolPage />} />
        <Route path="/fieldNotebook" element={<FieldNotebookPage />} />
        <Route path="/tools/updateTool/:id" element={<UpdateToolPage />} />
        <Route path="/toolRequest" element={<AddToolRequestPage />} />
        <Route path="/fieldNotebook/addNote" element={<AddNotePage />} />
      </Route>
      {/* route esterna protetta, piattaforma Digital twin */}
      <Route
        element={
          <ProtectedRoute>
            <DigitalTwinLayout>
              <Outlet />
            </DigitalTwinLayout>
          </ProtectedRoute>
        }
      >
        <Route path="/digitalTwin" element={<DigitalTwinPage />} />
        <Route path="/digitalTwin/cropDetail" element={<CropDetailPage />} />
        <Route path="/digitalTwin/:id" element={<CropDetailPage />} />
        <Route
          path="/digitalTwin/livestock/:id"
          element={<LivestockDetailPage />}
        />
      </Route>

      {/* Route protette - con prefisso /admin */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout>
              <Outlet />
            </MainLayout>
          </ProtectedRoute>
        }
      >
        <Route path="/admin/home" element={<HomePage />} />
        <Route path="/admin/users" element={<UsersPage />} />
        <Route path="/admin/users/addUser" element={<AddUserPage />} />
        <Route path="/admin/lands" element={<LandsPage />} />
        <Route path="/admin/fieldsType" element={<FieldsTypePage />} />
        <Route path="/admin/settings" element={<SettingsPage />} />
        <Route path="/admin/cultureList" element={<CultureListPage />} />
        <Route path="/admin/irrigationType" element={<IrrigationTypePage />} />
        <Route
          path="/admin/lands/updateField/:id"
          element={<UpdateFieldPage />}
        />
        <Route
          path="/admin/cultureList/addCulture"
          element={<AddCulturePage />}
        />
        <Route
          path="/admin/cultureList/updateCulture/:id"
          element={<UpdateCulturePage />}
        />
        <Route
          path="/admin/users/updateUser/:id"
          element={<UpdateUserPage />}
        />
        <Route
          path="/admin/irrigationType/addIrrigation"
          element={<AddIrrigationPage />}
        />
        <Route
          path="/admin/irrigationType/updateIrrigation/:id"
          element={<UpdateIrrigationPage />}
        />
        <Route
          path="/admin/fieldsType/addFieldType"
          element={<AddFieldTypePage />}
        />
        <Route
          path="/admin/fieldsType/updateFieldType/:id"
          element={<UpdateFieldTypePage />}
        />
      </Route>
      <Route
        path="*"
        element={
          <ProtectedRoute isNotFoundPage={true}>
            <MainLayout>
              <NotFoundPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const Router = () => (
  <Routes>
    <Route
      path={getRouterPaths().monitoringSection}
      element={<MonitoringSection />}
    />
    <Route path="*" element={<ApplicationRoutes />} />
  </Routes>
);

export default Router;
